import { LanguageToProviderTypeMap } from '@common/Fabric/constants';
import { FabricProviderType } from '@common/Fabric/types';
import { ORCHESTRATION_PROVIDER } from '@common/Fabric/types/orchestration';
import { useUserTeams } from '@common/projectHooks';
import { FileFormats } from '@common/Transpiler/types';
import { removeEmptyParams } from '@prophecy/utils/data';
import { useOnce } from '@prophecy/utils/react/hooks';
import { ResultOf } from 'gql.tada';

import { SQL_PROVIDER } from '../constants/sql';
import { makeGetCall, makeGraphQlCall } from '../data/util';
import { BackEndCodeLanguage, GitCombinedStorageModel } from '../graphqlTypes/enums';
import { graphql } from '../graphqlTypes/graphql';
import { hostUrl } from '../utils/getServerUrl';
import { useAppMetadata } from './appMetadata';
import { EditionConfig } from './editionConfigType';

async function getEditionConfig() {
  return makeGetCall(hostUrl + '/api/oem/config') as Promise<{
    data: EditionConfig;
    success: boolean;
  }>;
}

const getUrlToWorkspaceMappings = graphql(`
  query getUrlToWorkspaceMappings {
    getUrlToWorkspaceMappings {
      url
      workspace
      dataPlaneUrl
    }
  }
`);

export type URLToWorkspaceMapping = ResultOf<typeof getUrlToWorkspaceMappings>['getUrlToWorkspaceMappings'][number];

const getOEMUrlMapping = async () => {
  return makeGraphQlCall<ResultOf<typeof getUrlToWorkspaceMappings>>(getUrlToWorkspaceMappings);
};

export const DefaultEditionConfig: EditionConfig = {
  dataPlaneUrl: hostUrl,
  allowFabricCreation: true,
  supportedLanguages: Object.values(BackEndCodeLanguage),
  supportedGitStorageModels: Object.values(GitCombinedStorageModel),
  supportedFabricProviders: Object.values(FabricProviderType),
  // TODO: Refactor to use a separate project mapping for projects
  supportedSQLFabricTypes: [...Object.values(SQL_PROVIDER), ORCHESTRATION_PROVIDER.ProphecyManaged],
  supportedTranspilers: Object.values(FileFormats),
  teamsWithTranspilerAccess: [],
  isLineageEnabled: true,
  disableTranspiler: false,
  disabledMessage: 'Please contact support team.',
  isSubgraphEnabled: true,
  isModelEnabled: true,
  isSSOEnabled: true,
  isProjectTemplateEnabled: true
};

export async function fetchEditionConfig() {
  const [editionConfigRes, urlMappingRes] = await Promise.all([getEditionConfig(), getOEMUrlMapping()]);
  const editionConfig = editionConfigRes.success
    ? {
        ...DefaultEditionConfig,
        ...removeEmptyParams(editionConfigRes.data || {})
      }
    : DefaultEditionConfig;

  const workspaceUrl = hostUrl.replace(/\/$/, '');
  const mappings = urlMappingRes?.getUrlToWorkspaceMappings || [];
  const matchedMapping = mappings.find((mapping) => mapping.url === workspaceUrl);
  return { ...editionConfig, dataPlaneUrl: matchedMapping?.dataPlaneUrl || hostUrl };
}

export function useEditionConfig() {
  const appMetadata = useAppMetadata();
  return appMetadata.editionConfig;
}

export function useSupportedLanguages() {
  const editionConfig = useEditionConfig();
  return useOnce(() => new Set(editionConfig.supportedLanguages));
}

export function useSupportedGitStorageModels() {
  const editionConfig = useEditionConfig();
  return useOnce(() => new Set(editionConfig.supportedGitStorageModels));
}

export const useSupportedTranspilerTypes = () => {
  const editionConfig = useEditionConfig();
  return useOnce(() => new Set(editionConfig.supportedTranspilers));
};

export function useHasTranspilerAccess() {
  const editionConfig = useEditionConfig();
  const userTeams = useUserTeams().data;

  const { teamsWithTranspilerAccess } = editionConfig;
  if (!teamsWithTranspilerAccess?.length) {
    return undefined;
  }

  const userTeamNames = new Set(userTeams.map((team) => team.name));
  return teamsWithTranspilerAccess.some((team) => userTeamNames.has(team));
}

export const useSupportedFabricProviderTypes = () => {
  const editionConfig = useEditionConfig();

  return useOnce(() => {
    const supportedTypesByLanguage = new Set<FabricProviderType>();
    editionConfig.supportedLanguages.forEach((language) => {
      LanguageToProviderTypeMap[language].forEach((providerType) => {
        supportedTypesByLanguage.add(providerType);
      });
    });

    const supportedFabricProviderTypes = new Set<FabricProviderType>();
    editionConfig.supportedFabricProviders.forEach((provider) => {
      if (supportedTypesByLanguage.has(provider)) supportedFabricProviderTypes.add(provider);
    });

    return supportedFabricProviderTypes;
  });
};

type FabricTypes<T extends FabricProviderType> = T extends FabricProviderType.SQL ? Set<SQL_PROVIDER> : undefined;
export const useSupportedFabricTypes = <T extends FabricProviderType>(fabricProviderType: T) => {
  const editionConfig = useEditionConfig();

  return useOnce(() => {
    if (fabricProviderType === FabricProviderType.SQL) {
      return new Set(editionConfig.supportedSQLFabricTypes);
    }
  }) as FabricTypes<T>;
};

export const useIsSparkSupported = () => {
  const editionConfig = useEditionConfig();
  return useOnce(() => {
    return [BackEndCodeLanguage.python, BackEndCodeLanguage.scala].some((language) =>
      editionConfig.supportedLanguages.includes(language)
    );
  });
};

export const useIsSQLSupported = () => {
  const editionConfig = useEditionConfig();
  return useOnce(() => {
    return editionConfig.supportedLanguages.includes(BackEndCodeLanguage.sql);
  });
};
